import type { FieldRemote } from '@jira/polaris__remote-field/src/FieldRemote.types.tsx';
import { fireCompoundAnalyticsEvent } from '@jira/polaris__lib-analytics/src/fireCompoundAnalyticsEvent.tsx';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { StoreActionApi } from '@atlassian/react-sweet-state';
import type { State } from './FieldsUpdate.types.tsx';
import { performJiraConfigurationUpdate } from './performJiraConfigurationUpdate.tsx';
import type { UpdateValueDecorationProps } from './UpdateValueDecorationProps.types.tsx';

export const updateInternal = async (
	updateProps: UpdateValueDecorationProps,
	fieldRemote: FieldRemote,
	getState: StoreActionApi<State>['getState'],
	setState: StoreActionApi<State>['setState'],
	onDecorationUpdateFailed: (arg1: Error) => void,
	createAnalyticsEvent: CreateUIAnalyticsEvent,
): Promise<void> => {
	const { fieldKey, localDecorationId } = updateProps;
	const state = getState();
	const { fieldValueDecorations, fields } = state;
	const field = fields[fieldKey];
	const existingValueDecorations = fieldValueDecorations[fieldKey] || [];
	const itemIndex = existingValueDecorations.findIndex(
		(decoration) => decoration.localDecorationId === localDecorationId,
	);

	if (itemIndex === -1) {
		// noop update of non-existing id
		return;
	}

	const oldDecoration = existingValueDecorations[itemIndex];

	const newValueDecorations = [...existingValueDecorations];
	newValueDecorations[itemIndex] = {
		...oldDecoration,
		...updateProps,
	};

	setState({
		fieldValueDecorations: {
			...fieldValueDecorations,
			[fieldKey]: newValueDecorations,
		},
	});

	try {
		try {
			await performJiraConfigurationUpdate({
				fieldKey,
				fieldRemote,
				existingDecorations: existingValueDecorations,
				newDecorations: newValueDecorations,
			});

			fireCompoundAnalyticsEvent.FieldConfig.valueDecorationChanged(createAnalyticsEvent({}), {
				issueFieldKey: fieldKey,
				issueFieldType: field.type,
			});
		} catch (error) {
			setState({
				fieldValueDecorations: {
					...getState().fieldValueDecorations,
					[fieldKey]: existingValueDecorations,
				},
			});

			throw error;
		}
	} catch (error) {
		onDecorationUpdateFailed(
			error instanceof Error ? error : new Error('Failed to update value decoration'),
		);
	}
};
